import React, { useEffect, useMemo, useState } from 'react';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import {
    DDatePicker,
    DIdAutocomplete,
    DModal,
    PerformanceIcon,
} from '../../../dum';
import { DATE_FORMAT } from '../../../../helpers';
import { HOURLY_DATA_EPOCH } from '../../../smart/production-performance';
import { ProductionPerformanceAnalysis } from './production-performance-analysis';

// One product×machine line of the production, already deduped by the caller.
export interface PerformanceDialogLine {
    machineId: number;
    machineName: string;
    productId: number;
    productDescription: string;
}

interface IOrderProductionPerformanceDialog {
    open: boolean;
    setOpen: (open: boolean) => void;
    orderProductionId: number | null;
    lines: PerformanceDialogLine[];
}

/**
 * "Grade" view for one production record: the production-performance analysis
 * (same component as the Rendimiento page) locked to one of the record's
 * product×machine combos, with the record's own corridas highlighted so it
 * can be judged against the combo's history.
 *
 * Carries its own Desde/Hasta pair with the same default as the Rendimiento
 * page (`HOURLY_DATA_EPOCH` → today). Everything below reads that one window —
 * KPI, Gráfica and Corridas alike — so the dialog and the page agree when their
 * filters agree. Without it the dialog spanned all of history, and pre-epoch
 * corridas (kilos recorded, hours = 0) inflated the kg/hr headline.
 */
export function OrderProductionPerformanceDialog({
    open,
    setOpen,
    orderProductionId,
    lines,
}: IOrderProductionPerformanceDialog) {
    // 1-based so the id never collides with DIdAutocomplete's falsy checks.
    const [selectedLineId, setSelectedLineId] = useState<number | null>(null);

    // Same window as the Rendimiento page. Deliberately NOT reset when another
    // record is graded: one dialog instance serves the whole list, and a user
    // who widened the window is usually reviewing several rows under it.
    const [fromDate, setFromDate] = useState<string>(HOURLY_DATA_EPOCH);
    const [toDate, setToDate] = useState<string | null>(null);
    // Cleans only the packed KPI + scatter (single-product runs); Consumo kg/hr
    // stays over all runs so it still matches the Rendimiento tab.
    const [singleProductOnly, setSingleProductOnly] = useState(false);

    // A new record was picked → land on its first combo.
    useEffect(() => {
        setSelectedLineId(lines.length > 0 ? 1 : null);
    }, [orderProductionId, lines.length]);

    const lineOptions = useMemo(
        () =>
            lines.map((line, index) => ({
                id: index + 1,
                name: `${line.productDescription} · ${line.machineName}`,
            })),
        [lines],
    );

    const selectedLine =
        selectedLineId != null ? lines[selectedLineId - 1] : undefined;

    return (
        <DModal
            icon={<PerformanceIcon />}
            title={'Rendimiento de la orden de producción'}
            open={open}
            setOpen={setOpen}
            dismissible
        >
            <Box sx={{ px: 2, py: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        flexWrap: 'wrap',
                        alignItems: 'flex-end',
                        mb: 1,
                    }}
                >
                    {lines.length > 1 ? (
                        <Box sx={{ maxWidth: 480 }}>
                            <DIdAutocomplete
                                id={selectedLineId}
                                onChange={setSelectedLineId}
                                itemText={'name'}
                                label={'Producto · máquina'}
                                options={lineOptions}
                            />
                        </Box>
                    ) : null}
                    <Box sx={{ width: 180 }}>
                        <DDatePicker
                            views={['day']}
                            inputFormat={DATE_FORMAT}
                            label={'Desde'}
                            value={fromDate}
                            clearable={false}
                            disableGutters
                            onChange={(val) => {
                                if (val) setFromDate(val);
                            }}
                        />
                    </Box>
                    <Box sx={{ width: 180 }}>
                        <DDatePicker
                            views={['day']}
                            inputFormat={DATE_FORMAT}
                            label={'Hasta'}
                            value={toDate ?? ''}
                            clearable
                            disableGutters
                            onChange={(val) => setToDate(val ?? null)}
                        />
                    </Box>
                    <FormControlLabel
                        control={
                            <Checkbox
                                size={'small'}
                                checked={singleProductOnly}
                                onChange={(e) =>
                                    setSingleProductOnly(e.target.checked)
                                }
                            />
                        }
                        label={
                            <Typography variant={'caption'}>
                                Solo producciones de un solo producto
                            </Typography>
                        }
                    />
                </Box>
                <ProductionPerformanceAnalysis
                    machineId={selectedLine?.machineId ?? null}
                    productId={selectedLine?.productId ?? null}
                    fromDate={fromDate}
                    toDate={toDate}
                    highlightProductionId={orderProductionId}
                    singleProductOnly={singleProductOnly}
                />
            </Box>
        </DModal>
    );
}
